import { c as _c } from "react/compiler-runtime";
import { css, cx, sva } from '@repo/pandacss/css';
import { CloseIcon } from '@repo/ui/icons/CloseIcon';
import { EllipsisIcon } from '@repo/ui/icons/EllipsisIcon';
import { Typography } from '@repo/ui/Typography';
import Image from 'next/image';
import type { AsyncOptionGroup } from '@/shared/config/funnel/async-options/types';
const chipRecipe = sva({
  slots: ['wrapper', 'imageMask'],
  base: {
    wrapper: {
      position: 'relative',
      width: '[40px]',
      height: '[40px]',
      bg: 'common.white'
    },
    imageMask: {
      display: 'block',
      width: '[100%]',
      height: '[100%]',
      position: 'relative',
      overflow: 'hidden'
    }
  },
  variants: {
    variant: {
      circle: {
        wrapper: {
          rounded: 'full',
          border: '[0.5px solid {colors.neutral.lighter}]',
          p: '1'
        },
        imageMask: {
          rounded: 'full'
        }
      },
      square: {
        wrapper: {
          rounded: 'none',
          border: 'none',
          p: '0'
        },
        imageMask: {
          rounded: 'none'
        }
      }
    }
  }
});
type ChipProps = {
  item: AsyncOptionGroup['options'][number];
  onRemove: (value: string) => void;
  variant: 'circle' | 'square';
};
export function Chip(t0) {
  const $ = _c(28);
  const {
    item,
    onRemove,
    variant
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== item.imageInvert || $[1] !== item.imageURL || $[2] !== item.label || $[3] !== item.value || $[4] !== variant) {
    const {
      wrapper,
      imageMask
    } = chipRecipe({
      variant
    });
    const isOther = item.value === "other";
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = css({
        flexShrink: "[0]",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1",
        width: "[80px]"
      });
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t1 = cx(wrapper);
    t2 = isOther ? <span className={css({
      width: "[100%]",
      height: "[100%]",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    })}><EllipsisIcon /></span> : item.imageURL ? <span className={cx(imageMask)}><Image src={item.imageURL} alt="" fill={true} sizes="40px" className={css({
        width: "[100%]",
        height: "[100%]",
        objectFit: "contain",
        filter: item.imageInvert ? "[invert(1)]" : "[none]"
      })} /></span> : <Typography as="span" variant="body.small" css={{
      width: "[100%]",
      height: "[100%]",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bg: "greens.lightest",
      color: "greens.dark",
      fontWeight: "bold"
    }} aria-hidden="true">{item.label.charAt(0)}</Typography>;
    $[0] = item.imageInvert;
    $[1] = item.imageURL;
    $[2] = item.label;
    $[3] = item.value;
    $[4] = variant;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = css({
      position: "absolute",
      bottom: "[-4px]",
      right: "[-4px]",
      width: "[20px]",
      height: "[20px]",
      rounded: "full",
      p: "[0]",
      bg: "neutral.darker",
      color: "common.white",
      border: "[1px solid {colors.common.white}]",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      _hover: {
        bg: "neutral.darkest"
      }
    });
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== item.value || $[11] !== onRemove) {
    t5 = () => onRemove(item.value);
    $[10] = item.value;
    $[11] = onRemove;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = `Remove ${item.label}`;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CloseIcon fill="#FFFFFF" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6) {
    t8 = <button type="button" tabIndex={0} className={t4} onClick={t5} aria-label={t6}>{t7}</button>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t8) {
    t9 = <div className={t1}>{t2}{t8}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      color: "neutral.darker-2",
      fontWeight: "medium",
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      width: "[100%]"
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== item.label) {
    t11 = <Typography as="span" variant="body.small" css={t10} title={item.label}>{item.label}</Typography>;
    $[22] = item.label;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t3 || $[26] !== t9) {
    t12 = <div className={t3}>{t9}{t11}</div>;
    $[24] = t11;
    $[25] = t3;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}